import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { RequesterService } from './requester.service';
import { ApiToken } from '../models/apiToken';
import { ErrorService } from './error.service';
import { CustomAppConfig } from '@upassignations/custom-app-config';
import { ViewClass } from '../models/viewclass';
import { NavigationPath } from '../models/navigation-path';
/**
 * Service d'authentification.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  personalization$ = new BehaviorSubject(null);
  authorization$ = new BehaviorSubject(null);

  private logged = new Subject<boolean>();
  private userName: string;
  private cuid: string;

  constructor(
    // eslint-disable-next-line no-unused-vars
    private config: CustomAppConfig,
    // eslint-disable-next-line no-unused-vars
    private router: Router,
    // eslint-disable-next-line no-unused-vars
    private requesterService: RequesterService,
    // eslint-disable-next-line no-unused-vars
    private errorService: ErrorService
  ) {
    this.logged.next(false);
  }

  /**
   * Si le token d'authentification existe et est encore valide, alors true.
   */
  isLogged(): Observable<boolean> {
    return this.logged.asObservable();
  }

  /**
   * Retourne l'identifiant de l'utilisateur courant.
   *
   * @returns userName
   */
  getUserName(): string {
    return this.userName;
  }

  /**
   * Retourne le CUID de l'utilisateur courant.
   *
   * @returns CUID
   */
  getCUID(): string {
    return this.cuid;
  }

  /**
   * @returns an observable handling the authorization for a given user
   */
  getAuthorization$(cuid: string): Observable<any> {
    const data = new FormData();
    data.append('_viewClass', ViewClass.CHECK_AUTHORIZED);
    data.append('platform', 'Lexia');
    data.append('action', 'access');

    return this.requesterService
      .httpPostingSynchronously$(
        this.config.get('apiEndpoint') + '/api/execute',
        data,
        null
      )
      .pipe(
        map((response: any) => {
          if (response?.error) {
            this.errorService.setError(
              response.error.code,
              response.error.message
            );
          }
          return response;
        })
      );
  }

  /**
   * @returns an observable handling the personalization parameters for a given user
   */
  getPersonalization$(): Observable<any> {
    const data = new FormData();
    data.append('_viewClass', ViewClass.GET_USER_PROFILE);
    return this.requesterService
      .httpPostingSynchronously$(
        this.config.get('apiEndpoint') + '/api/execute',
        data
      )
      .pipe(
        map((response: any) => {
          if (response?.error) {
            this.errorService.setError(
              response.error.code,
              response.error.message
            );
          }
          return response;
        })
      );
  }

  /**
   * Authentification.
   */
  connexion(): Observable<boolean> {
    const apiToken = localStorage.getItem('apiToken');
    const helper = new JwtHelperService();

    if (apiToken && !helper.isTokenExpired(apiToken)) {
      this.cuid = helper.decodeToken(apiToken).sub;
      this.logged.next(true);
      return of(true);
    }

    return this.requesterService
      .httpGettingSynchronously$(
        this.config.get('apiEndpoint') + '/auth/token',
        { withCredentials: true },
        true
      )
      .pipe(
        map((response: any) => {
          const api = response as ApiToken;

          if (!api.token || helper.isTokenExpired(api.token)) {
            throw new Error('Invalid or expired token');
          }

          this.cuid = helper.decodeToken(api.token).sub;
          localStorage.setItem('apiToken', api.token);

          this.getAuthorization$(this.cuid).subscribe((res: any) => {
            this.authorization$.next(res);
            if (res && res.authorized) {
              this.router.navigate([NavigationPath.TrackingTable]);
            } else {
              localStorage.removeItem('apiToken');
              this.router.navigate([NavigationPath.UnAuthorised]);
            }
          });

          this.logged.next(true);
          return true;
        }),
        catchError((error) => {
          console.error('Erreur de connexion', error);

          const url = this.config.get('ssoEndpoint');
          window.location.assign(url);
          return of(false);
        })
      );
  }
}
